import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Locale } from '@/lib/api/types';

/**
 * The chrome locale this browser last used, cached so a reload does not paint the console
 * in English before `/admin/me` answers.
 *
 * The profile is the source of truth — it follows an editor to another machine — and
 * {@link applyChromeLocale} is called again with `user.locale` as soon as the session loads.
 * This is the fast path, and the per-device half of the header toggle.
 *
 * localStorage throws in some privacy modes; an unreadable preference is the same as none.
 */
export const LOCALE_KEY = 'tepco.locale';

function readStoredLocale(): Locale | null {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(LOCALE_KEY);
    return raw === 'ar' || raw === 'en' ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: Locale): void {
  try {
    localStorage?.setItem(LOCALE_KEY, locale);
  } catch {
    /* Quota or a locked-down profile — the cache is an optimisation, not state. */
  }
}

/**
 * The copy is fetched, not bundled into the entry chunk. Imported statically, both
 * catalogs landed in the eager chunk, so every visitor downloaded BOTH languages before
 * first paint. Split, the active language is one small chunk fetched in parallel with the
 * app, and the other is fetched only if someone switches to it.
 *
 * `en` is always loaded because it is the fallback: an Arabic key the catalog has not
 * translated must render English, not its own key.
 */
const bundles: Record<Locale, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./locales/en'),
  ar: () => import('./locales/ar'),
};

export async function loadLocale(locale: Locale): Promise<void> {
  if (i18n.hasResourceBundle(locale, 'translation')) return;
  const mod = await bundles[locale]();
  i18n.addResourceBundle(locale, 'translation', mod.default, true, true);
}

/**
 * Boot i18next with the language this browser last used, and wait for its copy before the
 * first render — a console that paints its keys and then swaps them for words is worse
 * than one that waits a frame for the words. Boot used to hard-code `'en'`, so an Arabic
 * editor got an English LTR console on every reload.
 */
export async function initI18n(): Promise<void> {
  const initial = readStoredLocale() ?? 'en';
  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      resources: {},
      lng: initial,
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  }
  await Promise.all(initial === 'en' ? [loadLocale('en')] : [loadLocale('en'), loadLocale(initial)]);
  await i18n.changeLanguage(initial);
  applyChromeLocale(initial, { persist: false });
}

/**
 * The writing direction a chrome locale is read in.
 *
 * Exported because `<html dir>` is not the only consumer: Radix primitives take their own
 * `dir` and default to `"ltr"` when it is absent, regardless of what the document says — a
 * Radix subtree renders a real `dir="ltr"` element, which OVERRIDES the inherited direction.
 * So the direction is handed to them explicitly (`DirectionProvider` plus `dir` on each
 * portalled primitive), and it comes from here, in one place.
 */
export function directionFor(locale: Locale | (string & {})): 'rtl' | 'ltr' {
  // Widened past `Locale`: i18next types `language` as a plain string. Anything that is not
  // a language we ship reads left-to-right, which is also the fallback locale.
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Apply chrome locale: `<html lang/dir>` + i18next language (spec §5.3 / §6).
 *
 * `persist: false` is for the caller that is only replaying what was already stored.
 */
export function applyChromeLocale(locale: Locale, opts?: { persist?: boolean }): void {
  // Set first and synchronously — the direction of the page must not wait on a network
  // fetch of the copy.
  // Guarded for the node-environment tests that load the shared setup without a DOM.
  if (typeof document !== 'undefined') {
    const html = document.documentElement;
    html.setAttribute('lang', locale);
    html.setAttribute('dir', directionFor(locale));
  }
  if (opts?.persist !== false) writeStoredLocale(locale);
  void loadLocale(locale)
    .then(() => i18n.changeLanguage(locale))
    // A chunk that will not load leaves the console in the language it is already
    // showing, which is the only safe failure here.
    .catch(() => undefined);
}

export default i18n;
